import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { usePeriodStore } from '@/stores/periodStore'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { PeriodStatusBadge } from '@/components/period/PeriodStatusBadge'
import { UnlockPeriodDialog } from '@/components/period/UnlockPeriodDialog'
import { Lock, Unlock, AlertCircle, CheckCircle } from 'lucide-react'

/**
 * Developer test page for manually testing period locking functionality
 * This is a temporary tool for testing - not part of the final app UI
 */
export default function PeriodLockingTestPage() {
  const { currentUser } = useAuth()
  const { periods, loading, loadPeriods, lockPeriod, lockingPeriod } = usePeriodStore()

  const [selectedPeriod, setSelectedPeriod] = useState('2025-10-01')
  const [lockReason, setLockReason] = useState('Manual test')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [showUnlockDialog, setShowUnlockDialog] = useState(false)

  useEffect(() => {
    if (currentUser) {
      loadPeriods(currentUser)
    }
  }, [currentUser, loadPeriods])

  const handleLockPeriod = async () => {
    if (!currentUser) {
      setMessage({ type: 'error', text: 'User not authenticated' })
      return
    }

    try {
      setMessage(null)
      await lockPeriod(currentUser, selectedPeriod, lockReason)
      setMessage({ type: 'success', text: `Period ${selectedPeriod} locked successfully!` })
      // Reload periods to get updated status
      await loadPeriods(currentUser)
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to lock period'
      })
    }
  }

  const getPeriodStatus = (period: string) => {
    const periodData = periods.find(p => p.id === period)
    return periodData?.status || null
  }

  if (!currentUser) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Please log in to test period locking</AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Period Locking Test Page</h1>
        <p className="text-muted-foreground">
          Developer tool for testing period locking functionality
        </p>
      </div>

      {/* Lock Period Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Lock Period</CardTitle>
          <CardDescription>
            Lock a period to prevent edits to earnings and compensations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="period">Period (yyyy-mm-01)</Label>
            <Input
              id="period"
              type="text"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              placeholder="2025-10-01"
            />
            <p className="text-sm text-muted-foreground">
              Current status: {getPeriodStatus(selectedPeriod) ? (
                <PeriodStatusBadge status={getPeriodStatus(selectedPeriod)!} />
              ) : (
                <span className="text-muted-foreground">Not found (will be created)</span>
              )}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason (optional)</Label>
            <Textarea
              id="reason"
              value={lockReason}
              onChange={(e) => setLockReason(e.target.value)}
              placeholder="e.g., Manual test, End of month closing"
              rows={2}
            />
          </div>

          <Button
            onClick={handleLockPeriod}
            disabled={lockingPeriod || !selectedPeriod}
            className="w-full"
          >
            {lockingPeriod ? (
              <>
                <Lock className="mr-2 h-4 w-4 animate-spin" />
                Locking...
              </>
            ) : (
              <>
                <Lock className="mr-2 h-4 w-4" />
                Lock Period
              </>
            )}
          </Button>

          {message && (
            <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
              {message.type === 'success' ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertDescription>{message.text}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Unlock Period Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Unlock Period</CardTitle>
          <CardDescription>
            Unlock a locked period (requires mandatory reason)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={() => setShowUnlockDialog(true)}
            variant="outline"
            disabled={!getPeriodStatus(selectedPeriod) || getPeriodStatus(selectedPeriod) !== 'locked'}
            className="w-full"
          >
            <Unlock className="mr-2 h-4 w-4" />
            Unlock Period {selectedPeriod}
          </Button>
          {getPeriodStatus(selectedPeriod) !== 'locked' && (
            <p className="text-sm text-muted-foreground mt-2">
              Period must be locked to unlock it
            </p>
          )}
        </CardContent>
      </Card>

      {/* All Periods List */}
      <Card>
        <CardHeader>
          <CardTitle>All Periods</CardTitle>
          <CardDescription>
            Showing {periods.length} period{periods.length !== 1 ? 's' : ''} in database
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground">Loading periods...</p>
          ) : periods.length === 0 ? (
            <p className="text-muted-foreground">No periods found. Lock a period to create one.</p>
          ) : (
            <div className="space-y-2">
              {periods.map((period) => (
                <div
                  key={period.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{period.id}</span>
                    <PeriodStatusBadge status={period.status} />
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {period.status === 'locked' && period.lockedAt && (
                      <span>
                        Locked {
                          typeof period.lockedAt === 'object' && 'toDate' in period.lockedAt
                            ? new Date(period.lockedAt.toDate()).toLocaleString('sv-SE')
                            : new Date(period.lockedAt as any).toLocaleString('sv-SE')
                        }
                      </span>
                    )}
                    {period.unlockHistory && period.unlockHistory.length > 0 && (
                      <span className="ml-2">
                        ({period.unlockHistory.length} unlock{period.unlockHistory.length !== 1 ? 's' : ''})
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unlock Dialog */}
      <UnlockPeriodDialog
        open={showUnlockDialog}
        period={selectedPeriod}
        onClose={() => {
          setShowUnlockDialog(false)
          // Reload periods after unlock
          if (currentUser) {
            loadPeriods(currentUser)
          }
        }}
      />

      {/* Instructions */}
      <Alert className="mt-6">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>How to test:</strong>
          <ol className="list-decimal list-inside mt-2 space-y-1">
            <li>Enter a period (format: yyyy-mm-01)</li>
            <li>Click "Lock Period" to lock it (creates snapshots for all drivers)</li>
            <li>Try to edit an earning from that period - it should fail</li>
            <li>Click "Unlock Period" and provide a reason</li>
            <li>Now you can edit earnings again</li>
          </ol>
        </AlertDescription>
      </Alert>
    </div>
  )
}
